import React, { useEffect, useRef, useState } from "react";
import { FaImage, FaTrashAlt, FaUpload } from "react-icons/fa";

const ACCEPT = "image/jpeg,image/png,image/webp,image/gif,image/avif";
const MAX_BYTES = 8 * 1024 * 1024;

/**
 * Tek resim seçme alanı — sürükle-bırak + tıkla, önizleme, kaldır.
 *
 * Dosyayı KENDİSİ yüklemez: seçilen File'ı yukarı verir, yükleme kaydetme
 * akışında yapılır. Yeni kayıtta henüz id olmadığı için resim ancak kayıt
 * oluştuktan sonra gönderilebilir.
 *
 * @param {string} [value] - Mevcut kayıtlı görselin URL'i
 * @param {File} [file] - Seçili ama henüz yüklenmemiş dosya
 * @param {Function} onChange - (File|null) — null = mevcut görseli kaldır
 * @param {boolean} [disabled]
 */
const ImageUploadField = ({
  label = "Görsel",
  hint,
  value,
  file,
  onChange,
  disabled = false,
  className = "",
}) => {
  const inputRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");
  const [dragging, setDragging] = useState(false);

  // Seçilen dosyanın önizlemesi. createObjectURL kullanılmıyor: CSP img-src'de
  // blob: yok (helmet.js), data: var — bu yüzden FileReader ile okunuyor.
  useEffect(() => {
    if (!file) {
      setPreview(null);
      return undefined;
    }
    let cancelled = false;
    const reader = new FileReader();
    reader.onload = () => {
      if (!cancelled) setPreview(String(reader.result));
    };
    reader.readAsDataURL(file);
    return () => {
      cancelled = true;
      reader.abort();
    };
  }, [file]);

  const accept = (picked) => {
    setError("");
    if (!picked) return;
    if (!picked.type.startsWith("image/")) {
      setError("Yalnızca resim dosyası seçilebilir");
      return;
    }
    if (picked.size > MAX_BYTES) {
      setError(`Dosya çok büyük (en fazla ${MAX_BYTES / 1024 / 1024}MB)`);
      return;
    }
    onChange(picked);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    if (disabled) return;
    accept(e.dataTransfer.files?.[0]);
  };

  const clear = () => {
    setError("");
    if (inputRef.current) inputRef.current.value = "";
    onChange(null);
  };

  const shown = preview || value || null;

  return (
    <div className={className}>
      {label && (
        <label className="block text-xs font-semibold text-gray-500 mb-1.5">
          {label}
          {hint && <span className="ml-1 font-normal text-gray-400">{hint}</span>}
        </label>
      )}

      <div className="flex items-start gap-3">
        <button
          type="button"
          onClick={() => !disabled && inputRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            if (!disabled) setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={handleDrop}
          disabled={disabled}
          className={`relative w-24 h-24 shrink-0 rounded-lg border-2 border-dashed flex items-center justify-center overflow-hidden transition-colors ${
            dragging
              ? "border-indigo-400 bg-indigo-50"
              : "border-gray-200 bg-gray-50 hover:border-gray-300"
          } disabled:opacity-60 disabled:cursor-not-allowed`}
        >
          {shown ? (
            <img
              src={shown}
              alt=""
              className="w-full h-full object-cover"
            />
          ) : (
            <FaImage className="text-gray-300 text-2xl" />
          )}
        </button>

        <div className="flex flex-col gap-1.5 pt-1">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => !disabled && inputRef.current?.click()}
              disabled={disabled}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              <FaUpload className="text-[10px]" />
              {shown ? "Değiştir" : "Resim Seç"}
            </button>
            {shown && (
              <button
                type="button"
                onClick={clear}
                disabled={disabled}
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold text-red-600 bg-white border border-red-200 rounded-lg hover:bg-red-50 disabled:opacity-50"
              >
                <FaTrashAlt className="text-[10px]" />
                Kaldır
              </button>
            )}
          </div>
          {error ? (
            <p className="text-[10px] font-semibold text-red-500">{error}</p>
          ) : (
            <p className="text-[10px] text-gray-400">
              JPEG/PNG/WebP · sürükleyip bırakabilirsiniz. Yüklenen görsel
              otomatik olarak 800px&apos;e küçültülür.
            </p>
          )}
        </div>
      </div>

      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT}
        className="hidden"
        onChange={(e) => accept(e.target.files?.[0])}
      />
    </div>
  );
};

export default ImageUploadField;
